import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Link,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Navigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { useCallProtocol } from '../../hooks/useCallProtocol';
import { isFeatureEnabled } from '../../config/featureFlags';
import CallProtocolDialog, { CATEGORY_LABELS, OUTCOME_LABELS, labelFor } from '../CallProtocol/CallProtocolDialog';

const SOURCE_LABELS = {
  direct: 'Direkt',
  ai_transfer: 'KI-Übergabe',
  manual: 'Manuell',
};

// Past call protocols. Shows own protocols; with callProtocol:read:all the whole team's.
// The same dialog as the ringing popup is reused for editing, so both stay in sync.
export default function CallProtocols() {
  const { listProtocols, getProtocol, createProtocol, isLoading } = useCallProtocol();

  const [protocols, setProtocols] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('');
  const [caller, setCaller] = useState('');
  const [from, setFrom] = useState(dayjs().startOf('month').format('YYYY-MM-DD'));
  const [to, setTo] = useState(dayjs().endOf('month').format('YYYY-MM-DD'));

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [aiContext, setAiContext] = useState(null);
  const [callContext, setCallContext] = useState(null);
  const [bookingContext, setBookingContext] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await listProtocols({ status: status || undefined, caller: caller || undefined, from, to });
      setProtocols(res?.protocols || []);
      setTotal(res?.total || 0);
    } catch {
      // useCallProtocol already surfaced the error.
    }
  }, [listProtocols, status, caller, from, to]);

  useEffect(() => {
    load();
  }, [load]);

  const openProtocol = async protocolId => {
    try {
      const res = await getProtocol(protocolId);
      setSelected(res?.protocol || null);
      setAiContext(res?.aiContext || null);
      setCallContext(res?.callContext || null);
      setBookingContext(res?.bookingContext || null);
      setDialogOpen(true);
    } catch {
      // handled in the hook
    }
  };

  // A protocol for a conversation that never came through Comdesk — a direct line, a callback
  // made by hand, or a note added afterwards.
  const createManual = async () => {
    try {
      const res = await createProtocol(undefined);
      setSelected(res?.protocol || null);
      setAiContext(null);
      setCallContext(null);
      setBookingContext(null);
      setDialogOpen(true);
      load();
    } catch {
      // handled in the hook
    }
  };

  if (!isFeatureEnabled('callProtocol')) return <Navigate to='/welcome' replace />;

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mb: 2 }}>
        <Typography variant='h5' component='h1' sx={{ fontWeight: 600 }}>
          Gesprächsprotokolle
        </Typography>
        <Button variant='contained' onClick={createManual} disabled={isLoading}>
          Neues Protokoll
        </Button>
      </Stack>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            label='Von'
            type='date'
            value={from}
            onChange={e => setFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label='Bis'
            type='date'
            value={to}
            onChange={e => setTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField select label='Status' value={status} onChange={e => setStatus(e.target.value)} sx={{ minWidth: 160 }}>
            <MenuItem value=''>Alle</MenuItem>
            <MenuItem value='draft'>Entwurf</MenuItem>
            <MenuItem value='completed'>Abgeschlossen</MenuItem>
          </TextField>
          <TextField
            label='Anrufer'
            defaultValue={caller}
            onBlur={e => setCaller(e.target.value)}
            sx={{ minWidth: 200 }}
          />
        </Stack>
      </Paper>

      <TableContainer component={Paper}>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Zeitpunkt</TableCell>
              <TableCell>Anrufer</TableCell>
              <TableCell>Buchung</TableCell>
              <TableCell>Herkunft</TableCell>
              <TableCell>Anliegen</TableCell>
              <TableCell>Ergebnis</TableCell>
              <TableCell>Mitarbeiter</TableCell>
              <TableCell>Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {protocols.map(p => (
              <TableRow key={p._id} hover>
                <TableCell>{p.meta?.createdAt ? dayjs(p.meta.createdAt).format('DD.MM.YYYY HH:mm') : '—'}</TableCell>
                <TableCell>{p.caller || '—'}</TableCell>
                <TableCell>
                  {p.bookingNumber ? (
                    <Link
                      href={`/internalBookings?bookingNumber=${encodeURIComponent(p.bookingNumber)}`}
                      target='_blank'
                      rel='noopener'
                      underline='hover'
                      // A number without a resolved id never matched a booking — say so instead of
                      // offering a link that leads nowhere useful.
                      color={p.bookingId ? 'primary' : 'text.secondary'}
                      title={p.bookingId ? undefined : 'Zu dieser Nummer wurde keine Buchung gefunden'}
                    >
                      {p.bookingNumber}
                    </Link>
                  ) : (
                    '—'
                  )}
                </TableCell>
                <TableCell>
                  <Chip size='small' label={SOURCE_LABELS[p.source] || p.source} />
                </TableCell>
                <TableCell>{p.category ? labelFor(CATEGORY_LABELS, p.category) : '—'}</TableCell>
                <TableCell>{p.outcome ? labelFor(OUTCOME_LABELS, p.outcome) : '—'}</TableCell>
                <TableCell>{p.agentName || '—'}</TableCell>
                <TableCell>
                  <Chip
                    size='small'
                    color={p.status === 'completed' ? 'success' : 'default'}
                    label={p.status === 'completed' ? 'Abgeschlossen' : 'Entwurf'}
                  />
                  {p.followUpRequired && p.status !== 'completed' && (
                    <Chip size='small' color='warning' label='Wiedervorlage' sx={{ ml: 0.5 }} />
                  )}
                </TableCell>
                <TableCell align='right'>
                  <Button size='small' onClick={() => openProtocol(p._id)}>
                    Öffnen
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {protocols.length === 0 && !isLoading && (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography variant='body2' sx={{ color: 'text.secondary', py: 2 }}>
                    Keine Gesprächsprotokolle im gewählten Zeitraum.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
        {`${protocols.length} von ${total} Protokollen`}
      </Typography>

      <CallProtocolDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        protocol={selected}
        aiContext={aiContext}
        callContext={callContext}
        bookingContext={bookingContext}
        onSaved={load}
      />
    </Box>
  );
}
